<template>
  <backoffice-container>
    <b-card class="px-1 pb-2">

      <!-- Send Email Title and Button -->
      <div class="d-flex justify-content-between flex-wrap my-1 mb-2">
        <h4>{{ $t('backoffice.email.send-email') || 'Send Email' }}</h4>
        <b-button
          :variant="form.testMode ? 'warning' : 'primary'"
          :disabled="!canSubmit || isLoading"
          @click="handleSubmit"
          :class="{'ml-1': !isSmall}"
          :block="isSmall"
        >
          <b-spinner v-if="isLoading" small class="mr-1" />
          <feather-icon v-if="form.testMode" icon="FlaskIcon" size="16" class="mr-1" />
          {{ form.testMode ? ($t('backoffice.email.test-email') || 'Test Email') : ($t('form.actions.send') || 'Send Email') }}
        </b-button>
      </div>

      <!-- Send Email Form -->
      <b-form @submit.prevent="handleSubmit">
        
        <!-- Email Type Selection -->
        <section class="mb-4">
          <h5 class="mb-3">{{ $t('backoffice.email.email-type') || 'Email Type' }}</h5>
          <b-form-group
            :label="$t('backoffice.email.select-email-type') || 'Select Email Type'"
            label-for="email-type"
          >
            <b-form-select
              id="email-type"
              v-model="form.emailType"
              :options="emailTypeOptions"
              :state="validation.emailType"
              required
            />
            <b-form-invalid-feedback>
              {{ $t('form.validation.required') || 'This field is required' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </section>

        <!-- Element Type for moderation emails -->
        <section v-if="showElementType" class="mb-4">
          <h5 class="mb-3">{{ $t('backoffice.email.element-type') || 'Element Type' }}</h5>
          <b-form-group
            :label="$t('backoffice.email.select-element-type') || 'Select Element Type'"
            label-for="element-type"
          >
            <b-form-select
              id="element-type"
              v-model="form.elementType"
              :options="elementTypeOptions"
              :state="validation.elementType"
              required
            />
            <b-form-invalid-feedback>
              {{ $t('form.validation.required') || 'This field is required' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </section>

        <!-- Conditional Fields depending on the email type -->
        <section v-if="conditionalFields.length > 0" class="mb-4">
          <h5 class="mb-3">{{ $t('backoffice.email.additional-fields') || 'Additional Fields' }}</h5>
          
          <!-- Custom Message Structure for better UX -->
          <div v-if="form.emailType === 'custom-message'" class="custom-message-form">
            <!-- Basic Structure Section -->
            <div class="field-section mb-4">
              <div class="section-header-row d-flex justify-content-between align-items-center">
                <h6 class="section-header mb-0">{{ $t('backoffice.email.basic-structure') || 'Basic Email Structure' }}</h6>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="toggleSection('basicStructure')"
                  class="toggle-btn"
                >
                  <feather-icon 
                    :icon="collapsedSections.basicStructure ? 'ChevronDownIcon' : 'ChevronUpIcon'" 
                    size="16" 
                  />
                </b-button>
              </div>
              <b-collapse :visible="!collapsedSections.basicStructure" class="mt-3">
                <div class="fields-container">
                  <div v-for="field in basicStructureFields" :key="field.key" class="field-item mb-3">
                    <template v-if="field.type === 'text' || field.type === 'email' || field.type === 'url' || field.type === 'number'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-input
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :type="field.type"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                    <template v-else-if="field.type === 'textarea'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-textarea
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                          :rows="field.rows || 3"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                  </div>
                </div>
              </b-collapse>
            </div>

            <!-- Section 1 -->
            <div class="field-section mb-4">
              <div class="section-header-row d-flex justify-content-between align-items-center">
                <h6 class="section-header mb-0">{{ $t('backoffice.email.section-one') || 'Section 1' }}</h6>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="toggleSection('section1')"
                  class="toggle-btn"
                >
                  <feather-icon 
                    :icon="collapsedSections.section1 ? 'ChevronDownIcon' : 'ChevronUpIcon'" 
                    size="16" 
                  />
                </b-button>
              </div>
              <b-collapse :visible="!collapsedSections.section1" class="mt-3">
                <div class="fields-container">
                  <div v-for="field in section1Fields" :key="field.key" class="field-item mb-3">
                    <template v-if="field.type === 'text' || field.type === 'email' || field.type === 'url' || field.type === 'number'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-input
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :type="field.type"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                    <template v-else-if="field.type === 'textarea'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-textarea
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                          :rows="field.rows || 3"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                  </div>
                </div>
              </b-collapse>
            </div>

            <!-- Section 2 -->
            <div class="field-section mb-4">
              <div class="section-header-row d-flex justify-content-between align-items-center">
                <h6 class="section-header mb-0">{{ $t('backoffice.email.section-two') || 'Section 2' }}</h6>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="toggleSection('section2')"
                  class="toggle-btn"
                >
                  <feather-icon 
                    :icon="collapsedSections.section2 ? 'ChevronDownIcon' : 'ChevronUpIcon'" 
                    size="16" 
                  />
                </b-button>
              </div>
              <b-collapse :visible="!collapsedSections.section2" class="mt-3">
                <div class="fields-container">
                  <div v-for="field in section2Fields" :key="field.key" class="field-item mb-3">
                    <template v-if="field.type === 'text' || field.type === 'email' || field.type === 'url' || field.type === 'number'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-input
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :type="field.type"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                    <template v-else-if="field.type === 'textarea'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-textarea
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                          :rows="field.rows || 3"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                  </div>
                </div>
              </b-collapse>
            </div>

            <!-- Other Fields -->
            <div class="field-section mb-4">
              <div class="section-header-row d-flex justify-content-between align-items-center">
                <h6 class="section-header mb-0">{{ $t('backoffice.email.other-fields') || 'Other Fields' }}</h6>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="toggleSection('otherFields')"
                  class="toggle-btn"
                >
                  <feather-icon 
                    :icon="collapsedSections.otherFields ? 'ChevronDownIcon' : 'ChevronUpIcon'" 
                    size="16" 
                  />
                </b-button>
              </div>
              <b-collapse :visible="!collapsedSections.otherFields" class="mt-3">
                <div class="fields-container">
                  <div v-for="field in otherFields" :key="field.key" class="field-item mb-3">
                    <template v-if="field.type === 'text' || field.type === 'email' || field.type === 'url' || field.type === 'number'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-input
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :type="field.type"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                    <template v-else-if="field.type === 'textarea'">
                      <b-form-group
                        :label="field.label"
                        :label-for="field.key"
                      >
                        <b-form-textarea
                          :id="field.key"
                          v-model="form.templateData[field.key]"
                          :placeholder="field.placeholder"
                          :state="validation[field.key]"
                          :required="field.required"
                          :rows="field.rows || 3"
                        />
                        <b-form-invalid-feedback v-if="field.required">
                          {{ $t('form.validation.required') || 'This field is required' }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </template>
                  </div>
                </div>
              </b-collapse>
            </div>
          </div>

          <!-- Standard Fields for other email types -->
          <transition-group v-else name="fade" tag="div">
            <div v-for="field in conditionalFields" :key="field.key" class="mb-3">
              <b-row v-if="field.type === 'text' || field.type === 'email' || field.type === 'url' || field.type === 'number'">
                <b-col :md="field.fullWidth ? 12 : 6">
                  <b-form-group
                    :label="field.label"
                    :label-for="field.key"
                  >
                    <b-form-input
                      :id="field.key"
                      v-model="form.templateData[field.key]"
                      :type="field.type"
                      :placeholder="field.placeholder"
                      :state="validation[field.key]"
                      :required="field.required"
                    />
                    <b-form-invalid-feedback v-if="field.required">
                      {{ $t('form.validation.required') || 'This field is required' }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row v-else-if="field.type === 'textarea'">
                <b-col :cols="field.fullWidth ? 12 : 12">
                  <b-form-group
                    :label="field.label"
                    :label-for="field.key"
                  >
                    <b-form-textarea
                      :id="field.key"
                      v-model="form.templateData[field.key]"
                      :placeholder="field.placeholder"
                      :state="validation[field.key]"
                      :required="field.required"
                      :rows="field.rows || 3"
                    />
                    <b-form-invalid-feedback v-if="field.required">
                      {{ $t('form.validation.required') || 'This field is required' }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row v-else-if="field.type === 'select'">
                <b-col :md="field.fullWidth ? 12 : 6">
                  <b-form-group
                    :label="field.label"
                    :label-for="field.key"
                  >
                    <b-form-select
                      :id="field.key"
                      v-model="form.templateData[field.key]"
                      :options="field.options"
                      :state="validation[field.key]"
                      :required="field.required"
                    />
                    <b-form-invalid-feedback v-if="field.required">
                      {{ $t('form.validation.required') || 'This field is required' }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row v-else-if="field.type === 'datetime'">
                <b-col :md="field.fullWidth ? 12 : 6">
                  <b-form-group
                    :label="field.label"
                    :label-for="field.key"
                  >
                    <b-form-input
                      :id="field.key"
                      v-model="form.templateData[field.key]"
                      type="datetime-local"
                      :state="validation[field.key]"
                      :required="field.required"
                    />
                    <b-form-invalid-feedback v-if="field.required">
                      {{ $t('form.validation.required') || 'This field is required' }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </b-col>
              </b-row>
              <div v-else-if="field.type === 'sessions'">
                <b-form-group
                  :label="field.label"
                  :label-for="field.key"
                >
                  <div class="border rounded p-3 mb-2">
                    <div v-for="(session, index) in form.templateData[field.key]" :key="index" class="mb-2">
                      <b-row>
                        <b-col md="4">
                          <b-form-input
                            v-model="session.browser"
                            placeholder="Browser"
                            class="mb-2"
                          />
                        </b-col>
                        <b-col md="4">
                          <b-form-input
                            v-model="session.os"
                            placeholder="Operating System"
                            class="mb-2"
                          />
                        </b-col>
                        <b-col md="3">
                          <b-form-input
                            v-model="session.lastActivity"
                            type="datetime-local"
                            class="mb-2"
                          />
                        </b-col>
                        <b-col md="1">
                          <b-button
                            variant="danger"
                            size="sm"
                            @click="removeSession(index)"
                          >
                            <feather-icon icon="XIcon" size="14" />
                          </b-button>
                        </b-col>
                      </b-row>
                    </div>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      @click="addSession"
                    >
                      <feather-icon icon="PlusIcon" size="14" class="mr-1" />
                      Add Session
                    </b-button>
                  </div>
                </b-form-group>
              </div>
            </div>
          </transition-group>
        </section>

        <!-- Separator -->
        <hr class="my-4">

        <!-- Community Selection -->
        <section class="mb-4">
          <h5 class="mb-3">{{ $t('backoffice.email.community-selection') || 'Community Selection' }}</h5>
          <b-form-group
            :label="$t('backoffice.email.select-community') || 'Select Community'"
            label-for="community"
          >
            <v-select
              id="community"
              v-model="form.communitySelected"
              :placeholder="$t('backoffice.email.placeholder.select-community') || 'Select community...'"
              :options="communityOptions"
              label="name"
              :reduce="option => option.slug"
              :get-option-label="option => option.name"
              :state="validation.communitySelected"
              required
              @input="handleCommunitySelect"
            />
            <b-form-invalid-feedback>
              {{ $t('form.validation.required') || 'This field is required' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </section>

        <!-- Member Selection -->
        <section class="mb-4">
          <h5 class="mb-3">{{ $t('backoffice.email.member-selection') || 'Member Selection' }}</h5>
          <b-form-group
            :label="supportsMultipleRecipients ? ($t('backoffice.email.select-members') || 'Select Members') : ($t('backoffice.email.select-member') || 'Select Member')"
            label-for="member"
          >
            <v-select
              id="member"
              v-model="form.memberSelected"
              :placeholder="supportsMultipleRecipients ? ($t('backoffice.email.placeholder.select-members') || 'Select members...') : ($t('backoffice.email.placeholder.select-member') || 'Select member...')"
              :options="allMemberOptions"
              label="name"
              :reduce="option => option.key"
              :get-option-label="option => option.name"
              :state="validation.memberSelected"
              :disabled="!form.communitySelected"
              :multiple="supportsMultipleRecipients"
              :loading="isSearching"
              :filterable="false"
              :searchable="true"
              :clearable="true"
              required
              @input="handleMemberSelect"
              @search="searchMembers"
              @search:blur="handleSearchBlur"
            >
              <template #option="option">
                <div class="d-flex align-items-center">
                  <b-avatar v-if="option.key !== 'custom'" size="sm" :src="option.avatarURL" class="mr-2" />
                  <feather-icon v-else icon="UserPlusIcon" size="16" class="mr-2" />
                  <span>{{ option.name }}</span>
                </div>
              </template>
            </v-select>
            <b-form-invalid-feedback>
              {{ $t('form.validation.required') || 'This field is required' }}
            </b-form-invalid-feedback>
            <small v-if="supportsMultipleRecipients" class="form-text text-muted">
              {{ $t('backoffice.email.multiple-recipients-hint') || 'You can select multiple members to send the same email to all of them' }}
            </small>
          </b-form-group>

          <!-- Custom Member Details (only show for single recipient emails) -->
          <div v-if="!supportsMultipleRecipients" class="mt-3">
            <h6 class="mb-3">
              {{ isCustomMember ? ($t('backoffice.email.custom-member-details') || 'Custom Member Details') : ($t('backoffice.email.selected-member-details') || 'Selected Member Details') }}
            </h6>
            
            <b-row>
              <b-col md="4">
                <b-form-group
                  :label="$t('backoffice.email.member-name') || 'Name'"
                  label-for="custom-name"
                >
                  <b-form-input
                    id="custom-name"
                    v-model="form.customMember.name"
                    :placeholder="$t('backoffice.email.placeholder.member-name') || 'Enter member name'"
                    :disabled="!isCustomMember"
                    :state="validation.customMemberName"
                    :required="isCustomMember"
                  />
                  <b-form-invalid-feedback v-if="isCustomMember">
                    {{ $t('form.validation.required') || 'This field is required' }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              
              <b-col md="4">
                <b-form-group
                  :label="$t('backoffice.email.member-email') || 'Email'"
                  label-for="custom-email"
                >
                  <b-form-input
                    id="custom-email"
                    v-model="form.customMember.email"
                    type="email"
                    :placeholder="$t('backoffice.email.placeholder.member-email') || 'Enter member email'"
                    :disabled="!isCustomMember"
                    :state="validation.customMemberEmail"
                    :required="isCustomMember"
                  />
                  <b-form-invalid-feedback v-if="isCustomMember">
                    {{ $t('form.validation.required') || 'This field is required' }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </b-col>
              
              <b-col md="4">
                <b-form-group
                  :label="$t('backoffice.email.member-username') || 'Username'"
                  label-for="custom-username"
                >
                  <b-form-input
                    id="custom-username"
                    v-model="form.customMember.username"
                    :placeholder="$t('backoffice.email.placeholder.member-username') || 'Enter username (optional)'"
                    :disabled="!isCustomMember"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>

          <!-- Selected Members Summary (for multiple recipients) -->
          <div v-if="supportsMultipleRecipients && selectedMembers.length > 0" class="mt-3">
            <h6 class="mb-3">{{ $t('backoffice.email.selected-members-summary') || 'Selected Members' }}</h6>
            <div class="selected-members-list">
              <div v-for="member in selectedMembers" :key="member.key" class="selected-member-item d-flex align-items-center mb-2 p-2 border rounded">
                <b-avatar v-if="member.key !== 'custom'" size="sm" :src="member.avatarURL" class="mr-2" />
                <feather-icon v-else icon="UserPlusIcon" size="16" class="mr-2" />
                <div class="flex-grow-1">
                  <div class="font-weight-medium">{{ member.name }}</div>
                  <small class="text-muted">{{ member.email }}</small>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="removeSelectedMember(member.key)"
                >
                  <feather-icon icon="XIcon" size="14" />
                </b-button>
              </div>
            </div>
            <small class="text-muted">
              {{ $t('backoffice.email.total-recipients', { count: selectedMembers.length }) || `Total recipients: ${selectedMembers.length}` }}
            </small>
          </div>
        </section>

        <!-- Language Selection (this at the end of the form) -->
        <section class="mb-4">
          <h5 class="mb-3">{{ $t('backoffice.email.language-selection') || 'Language Selection' }}</h5>
          <b-form-group
            :label="$t('backoffice.email.language') || 'Language'"
            label-for="language"
          >
            <b-form-select
              id="language"
              v-model="form.language"
              :options="languageOptions"
              :state="validation.language"
              required
            />
            <b-form-invalid-feedback>
              {{ $t('form.validation.required') || 'This field is required' }}
            </b-form-invalid-feedback>
          </b-form-group>
        </section>

        <!-- Test Mode Toggle (this at the end of the form) -->
        <section class="mb-4">
          <div class="d-flex align-items-center justify-content-between">
            <div>
              <h5 class="mb-1">{{ $t('backoffice.email.test-mode') || 'Test Mode' }}</h5>
              <small class="text-muted">{{ $t('backoffice.email.test-mode-description') || 'Enable to test email generation without sending' }}</small>
            </div>
            <b-form-checkbox
              v-model="form.testMode"
              switch
              size="lg"
              class="custom-switch-lg"
            >
              <span class="ml-2 font-weight-medium" :class="form.testMode ? 'text-warning' : 'text-muted'">
                {{ form.testMode ? ($t('backoffice.email.test-enabled') || 'Test Enabled') : ($t('backoffice.email.test-disabled') || 'Test Disabled') }}
              </span>
            </b-form-checkbox>
          </div>
          <b-alert v-if="form.testMode" show variant="warning" class="mt-3 mb-0">
            <div class="d-flex align-items-center">
              <feather-icon icon="AlertTriangleIcon" size="16" class="mr-2" />
              <small>
                <strong>{{ $t('backoffice.email.test-mode-warning') || 'Test Mode Active:' }}</strong>
                {{ $t('backoffice.email.test-mode-warning-text') || 'No email will be sent. You will only see the generated response.' }}
              </small>
            </div>
          </b-alert>
        </section>
      </b-form>
    </b-card>

    <!-- Information Box -->
    <b-card class="mt-3 border-info">
      <b-card-header class="bg-light-info mb-2">
        <div class="d-flex align-items-center">
          <feather-icon icon="InfoIcon" size="18" class="text-info mr-2" />
          <h6 class="mb-0 text-info">{{ $t('backoffice.email.info-title') || 'Email Feature Information' }}</h6>
        </div>
      </b-card-header>
      <b-card-body>
        <div class="text-sm">
          <div class="mb-2">
            <strong>{{ $t('backoffice.email.info-how-it-works') || 'How it works:' }}</strong>
            <p class="mb-2 text-muted">
              {{ $t('backoffice.email.info-description') || 'This feature allows you to send templated emails to community members using predefined email templates. Select a community, choose a member, pick an email template, and fill in the required information.' }}
            </p>
          </div>
          
          <div class="mb-2">
            <strong>{{ $t('backoffice.email.info-templates') || 'Available Templates:' }}</strong>
            <ul class="text-muted mb-2 pl-4">
              <li>{{ $t('backoffice.email.info-invitations') || 'Community invitations and welcomes' }}</li>
              <li>{{ $t('backoffice.email.info-notifications') || 'Post, comment, and forum notifications' }}</li>
              <li>{{ $t('backoffice.email.info-security') || 'Security alerts and password resets' }}</li>
              <li>{{ $t('backoffice.email.info-moderation') || 'Moderation results and pending reviews' }}</li>
              <li>{{ $t('backoffice.email.info-exports') || 'Export completion notifications' }}</li>
            </ul>
          </div>

          <div class="mb-2">
            <strong>{{ $t('backoffice.email.info-important-notes') || 'Important Notes:' }}</strong>
            <ul class="text-muted mb-0 pl-4">
              <li>{{ $t('backoffice.email.info-note-auth') || 'Emails are sent using your authentication credentials' }}</li>
              <li>{{ $t('backoffice.email.info-note-templates') || 'Template fields change based on the selected email type' }}</li>
              <li>{{ $t('backoffice.email.info-note-members') || 'Only members of the selected community will appear in the member list' }}</li>
              <li>{{ $t('backoffice.email.info-note-language') || 'Choose the appropriate language for the recipient' }}</li>
            </ul>
          </div>

          <b-alert show variant="warning" class="mb-0 mt-3">
            <div class="d-flex align-items-center">
              <feather-icon icon="AlertTriangleIcon" size="16" class="mr-2" />
              <small>
                <strong>{{ $t('backoffice.email.info-warning') || 'Warning:' }}</strong>
                {{ $t('backoffice.email.info-warning-text') || 'This will send a real email to the selected recipient. Please verify all information before sending.' }}
              </small>
            </div>
          </b-alert>
        </div>
      </b-card-body>
    </b-card>
  </backoffice-container>
</template>

<script>
import BackofficeContainer from '@/backoffice/components/BackofficeContainer.vue';
import vSelect from 'vue-select';
import { sendEmail } from '@/services/backend/email/emailService';
import { validatorEmailValidator } from '@/@core/utils/validations/validators';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import ToastNotificationsMixin from '@/@core/mixins/toast-notifications/ToastNotificationsMixin';
import { MainType } from '@copernicsw/community-common';


export default {
  name: 'SendEmail',
  components: {
    BackofficeContainer,
    vSelect,
  },
  mixins: [ToastNotificationsMixin],
  data() {
    return {
      isSmall: false,
      isLoading: false,
      form: {
        emailType: '',
        communitySelected: '',
        memberSelected: '',
        language: 'en',
        templateData: {},
        elementType: '',
        testMode: false,
        customMember: {
          name: '',
          email: '',
          username: '',
        },
      },
      validation: {
        emailType: null,
        communitySelected: null,
        memberSelected: null,
        language: null,
        elementType: null,
        customMemberName: null,
        customMemberEmail: null,
      },
      selectedCommunity: null,
      selectedMember: null,
      communityOptions: [],
      memberOptions: [],
      currentSpace: null,
      // Collapsed state for custom message sections
      collapsedSections: {
        basicStructure: true,
        section1: true,
        section2: true,
        otherFields: true,
      },
      // Add search-related state
      searchTimeout: null,
      isSearching: false,
      lastSearchTerm: '',
    };
  },
  computed: {
    isNexos() {
      return process.env.VUE_APP_MAIN_SPACE_SLUG === 'nexos';
    },
    emailTypeOptions() {
      return [
        { value: '', text: this.$t('form.select-placeholder') || 'Select email type...' },
        { value: 'invite-main', text: 'Invitation to Main Community' },
        { value: 'invite-sub', text: 'Invitation to Subcommunity' },
        { value: 'welcome-main', text: 'Welcome to Main Community' },
        { value: 'welcome-sub', text: 'Welcome to Subcommunity' },
        { value: 'new-topic', text: 'New Topic Notification' },
        { value: 'missing-member', text: 'Missing Member Notification' },
        { value: 'inactive-member', text: 'Inactive Member Notification' },
        { value: 'survey-1', text: 'Encuesta 1' },
        { value: 'survey-2', text: 'Encuesta 2' },
        { value: 'recognition-ambassadors-status-and-gift', text: 'Correo de Reconocimiento a Embajadores (status y regalo)' },
        { value: 'hall-of-fame-implemented', text: 'Rincón de la fama (ideas implementadas)' },
        { value: 'incentives-winner-20000', text: 'Ganador Incentivos 20.000' },
        { value: 'incentives-winner-30000', text: 'Ganador Incentivos 30.000' },
        { value: 'incentives-winner-50000', text: 'Ganador Incentivos 50.000' },
        { value: 'invitation-close-network', text: 'Invitación a Cercanos' },
        { value: 'newsletter-launch', text: 'Boletín Lanzamiento' },
        { value: 'newsletter-monthly-notification', text: 'Boletín notificación mensual' },
        { value: 'verify-email', text: 'Email Verification' },
        { value: 'security-alert', text: 'Security Alert' },
        { value: 'password-reset', text: 'Password Reset' },
        { value: 'space-created', text: 'Space Created' },
        { value: 'post', text: 'Post Created' },
        { value: 'forumAnswer', text: 'Forum Answer' },
        { value: 'moderation', text: 'Moderation Result' },
        { value: 'ModerationPending', text: 'Moderation Pending' },
        { value: 'comment', text: 'Comment Created' },
        { value: 'join-request', text: 'Join Request' },
        { value: 'export-completion', text: 'Export Completion' },
        { value: 'test-email', text: 'Test Email' },
        { value: 'custom-message', text: 'Custom Message' },
      ];
    },
    languageOptions() {
      // if it is Nexos, only Spanish and English are available
      if (this.isNexos) {
        return [
          { value: 'es', text: 'Español' },
          { value: 'en', text: 'English' },
        ];
      }
      // Other
      return [
        { value: 'en', text: 'English' },
        { value: 'es', text: 'Español' },
        { value: 'ca', text: 'Català' },
        // { value: 'bg', text: 'Български' },
        // { value: 'da', text: 'Dansk' },
        // { value: 'fr', text: 'Français' },
        // { value: 'de', text: 'Deutsch' },
        // { value: 'it', text: 'Italiano' },
        // { value: 'pt', text: 'Português' },
        // { value: 'ro', text: 'Română' },
        // { value: 'ru', text: 'Русский' },
        // { value: 'sv', text: 'Svenska' },
        // { value: 'tr', text: 'Türkçe' },
      ];
    },
    elementTypeOptions() {
      return [
        { value: '', text: this.$t('form.select-placeholder') || 'Select element type...' },
        { value: 'post', text: 'Post' },
        { value: 'forumAnswer', text: 'Forum Answer' },
        { value: 'comment', text: 'Comment' },
      ];
    },
    moderationStatusOptions() {
      return [
        { value: '', text: this.$t('form.select-placeholder') || 'Select status...' },
        { value: '1', text: 'Approved' },
        { value: '0', text: 'Rejected' },
      ];
    },
    conditionalFields() {
      const fields = [];
      const emailType = this.form.emailType;

      if (['invite-main', 'welcome-main'].includes(emailType)) {
        fields.push({
          key: 'memberName',
          label: this.$t('backoffice.email.member-name') || 'Member Name',
          type: 'text',
          placeholder: this.$t('backoffice.email.placeholder.member-name') || 'Enter member name',
          required: true,
        });
      }

      if (emailType === 'verify-email') {
        fields.push(
          {
            key: 'verificationCode',
            label: this.$t('backoffice.email.verification-code') || 'Verification Code',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.verification-code') || 'Enter verification code',
            required: true,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
            required: true,
          }
        );
      }

      if (emailType === 'security-alert') {
        fields.push(
          {
            key: 'deviceInfoBrowser',
            label: this.$t('backoffice.email.device-browser') || 'Device Browser',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.device-browser') || 'Enter browser info',
            required: true,
          },
          {
            key: 'deviceInfoOS',
            label: this.$t('backoffice.email.device-os') || 'Device OS',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.device-os') || 'Enter OS info',
            required: true,
          },
          {
            key: 'timestamp',
            label: this.$t('backoffice.email.timestamp') || 'Timestamp',
            type: 'datetime',
            required: true,
          },
          {
            key: 'differentDeviceSessions',
            label: this.$t('backoffice.email.different-sessions') || 'Different Device Sessions',
            type: 'sessions',
            required: false,
          }
        );
      }

      if (emailType === 'password-reset') {
        fields.push({
          key: 'url',
          label: this.$t('backoffice.email.url') || 'URL',
          type: 'url',
          placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
          required: true,
        });
      }

      if (emailType === 'spaceCreated') {
        fields.push(
          {
            key: 'spaceType',
            label: this.$t('backoffice.email.space-type') || 'Space Type',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.space-type') || 'Enter space type',
            required: true,
          },
          {
            key: 'spaceName',
            label: this.$t('backoffice.email.space-name') || 'Space Name',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.space-name') || 'Enter space name',
            required: true,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
            required: true,
          }
        );
      }

      if (['post', 'forumAnswer'].includes(emailType)) {
        fields.push(
          {
            key: 'memberName',
            label: this.$t('backoffice.email.member-name') || 'Member Name',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.member-name') || 'Enter member name',
            required: true,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
            required: true,
          }
        );
      }

      if (emailType === 'moderation') {
        fields.push(
          {
            key: 'moderationStatus',
            label: this.$t('backoffice.email.moderation-status') || 'Moderation Status',
            type: 'select',
            options: this.moderationStatusOptions,
            required: true,
          },
          {
            key: 'morphName',
            label: this.$t('backoffice.email.morph-name') || 'Morph Name',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.morph-name') || 'Enter morph name',
            required: true,
          },
          {
            key: 'feedbackForUser',
            label: this.$t('backoffice.email.feedback-user') || 'Feedback for User',
            type: 'textarea',
            placeholder: this.$t('backoffice.email.placeholder.feedback-user') || 'Enter feedback for user',
            required: false,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
            required: true,
          }
        );
      }

      if (emailType === 'ModerationPending') {
        fields.push({
          key: 'url',
          label: this.$t('backoffice.email.url') || 'URL',
          type: 'url',
          placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
          required: true,
        });
      }

      if (emailType === 'comment') {
        fields.push(
          {
            key: 'memberName',
            label: this.$t('backoffice.email.member-name') || 'Member Name',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.member-name') || 'Enter member name',
            required: true,
          },
          {
            key: 'morphType',
            label: this.$t('backoffice.email.morph-type') || 'Morph Type',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.morph-type') || 'Enter morph type',
            required: true,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
            required: true,
          }
        );
      }

      if (emailType === 'join-request') {
        fields.push(
          {
            key: 'memberName',
            label: this.$t('backoffice.email.member-name') || 'Member Name',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.member-name') || 'Enter member name',
            required: true,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
            required: true,
          }
        );
      }

      if (emailType === 'export-completion') {
        fields.push(
          {
            key: 'exportType',
            label: this.$t('backoffice.email.export-type') || 'Export Type',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.export-type') || 'Enter export type',
            required: true,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter URL',
            required: true,
          }
        );
      }

      if (emailType === 'test-email') {
        fields.push({
          key: 'testContent',
          label: this.$t('backoffice.email.test-content') || 'Test Email Content',
          type: 'textarea',
          placeholder: this.$t('backoffice.email.placeholder.test-content') || 'Enter test email content...',
          required: true,
          rows: 6,
        });
      }

      if (emailType === 'custom-message') {
        // Basic email structure fields
        fields.push(
          {
            key: 'subject',
            label: this.$t('backoffice.email.subject') || 'Subject',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.subject') || 'Enter email subject',
            required: true,
            fullWidth: true,
          },
          {
            key: 'preheader',
            label: this.$t('backoffice.email.preheader') || 'Preheader',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.preheader') || 'Enter email preheader text',
            required: false,
            fullWidth: true,
          },
          {
            key: 'title',
            label: this.$t('backoffice.email.title') || 'Main Title',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.title') || 'Enter main title',
            required: false,
            fullWidth: true,
          },
          {
            key: 'banner',
            label: this.$t('backoffice.email.banner') || 'Banner URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.banner') || 'Enter banner image URL',
            required: false,
            fullWidth: true,
          }
        );

        // Body content fields
        fields.push(
          {
            key: 'salutation',
            label: this.$t('backoffice.email.salutation') || 'Salutation',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.salutation') || 'Hello, Dear, etc.',
            required: false,
          },
          {
            key: 'sectionTitle',
            label: this.$t('backoffice.email.section-title') || 'Section Title',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section-title') || 'Enter section title',
            required: false,
          }
        );

        // Section 1 fields
        fields.push(
          {
            key: 'section1Title',
            label: this.$t('backoffice.email.section1-title') || 'Section 1 Title',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section1-title') || 'Enter section 1 title',
            required: false,
            fullWidth: true,
          },
          {
            key: 'section1Text',
            label: this.$t('backoffice.email.section1-text') || 'Section 1 Text',
            type: 'textarea',
            placeholder: this.$t('backoffice.email.placeholder.section1-text') || 'Enter section 1 content',
            required: false,
            rows: 4,
          },
          {
            key: 'section1CtaLabel',
            label: this.$t('backoffice.email.section1-cta-label') || 'Section 1 Button Text',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section1-cta-label') || 'Click here, Learn more, etc.',
            required: false,
          },
          {
            key: 'section1CtaLink',
            label: this.$t('backoffice.email.section1-cta-link') || 'Section 1 Button Link',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.section1-cta-link') || 'Enter button URL',
            required: false,
          }
        );

        // Section 1 Sublink fields
        fields.push(
          {
            key: 'section1SublinkText',
            label: this.$t('backoffice.email.section1-sublink-text') || 'Section 1 Sublink Text',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section1-sublink-text') || 'Enter sublink text',
            required: false,
          },
          {
            key: 'section1SublinkCodeText',
            label: this.$t('backoffice.email.section1-sublink-code-text') || 'Section 1 Code Text',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section1-sublink-code-text') || 'Enter code text',
            required: false,
          },
          {
            key: 'section1SublinkFeedback',
            label: this.$t('backoffice.email.section1-sublink-feedback') || 'Section 1 Feedback',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section1-sublink-feedback') || 'Enter feedback text',
            required: false,
          },
          {
            key: 'section1SublinkCode',
            label: this.$t('backoffice.email.section1-sublink-code') || 'Section 1 Code',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section1-sublink-code') || 'Enter verification code',
            required: false,
          }
        );

        // Section 2 fields
        fields.push(
          {
            key: 'section2Title',
            label: this.$t('backoffice.email.section2-title') || 'Section 2 Title',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section2-title') || 'Enter section 2 title',
            required: false,
            fullWidth: true,
          },
          {
            key: 'section2Text1',
            label: this.$t('backoffice.email.section2-text1') || 'Section 2 Text 1',
            type: 'textarea',
            placeholder: this.$t('backoffice.email.placeholder.section2-text1') || 'Enter section 2 first text',
            required: false,
            rows: 4,
          },
          {
            key: 'section2Card',
            label: this.$t('backoffice.email.section2-card') || 'Section 2 Card Content',
            type: 'textarea',
            placeholder: this.$t('backoffice.email.placeholder.section2-card') || 'Enter card content',
            required: false,
            rows: 3,
          },
          {
            key: 'section2Subcard',
            label: this.$t('backoffice.email.section2-subcard') || 'Section 2 Subcard',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section2-subcard') || 'Enter subcard text',
            required: false,
          },
          {
            key: 'section2ButtonText',
            label: this.$t('backoffice.email.section2-button-text') || 'Section 2 Button Text',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section2-button-text') || 'Enter button text',
            required: false,
          },
          {
            key: 'section2ButtonColor',
            label: this.$t('backoffice.email.section2-button-color') || 'Section 2 Button Color',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section2-button-color') || 'primary, secondary, etc.',
            required: false,
          }
        );

        // Section 2 Sublink fields
        fields.push(
          {
            key: 'section2SublinkText',
            label: this.$t('backoffice.email.section2-sublink-text') || 'Section 2 Sublink Text',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section2-sublink-text') || 'Enter sublink text',
            required: false,
          },
          {
            key: 'section2SublinkFeedback',
            label: this.$t('backoffice.email.section2-sublink-feedback') || 'Section 2 Feedback',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section2-sublink-feedback') || 'Enter feedback text',
            required: false,
          },
          {
            key: 'section2SublinkLink',
            label: this.$t('backoffice.email.section2-sublink-link') || 'Section 2 Sublink URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.section2-sublink-link') || 'Enter sublink URL',
            required: false,
          },
          {
            key: 'section2SublinkCodeText',
            label: this.$t('backoffice.email.section2-sublink-code-text') || 'Section 2 Code Text',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.section2-sublink-code-text') || 'Enter code text',
            required: false,
          }
        );

        // Terms and URL fields
        fields.push(
          {
            key: 'terms',
            label: this.$t('backoffice.email.terms') || 'Terms and Conditions',
            type: 'textarea',
            placeholder: this.$t('backoffice.email.placeholder.terms') || 'Enter terms and conditions text',
            required: false,
            rows: 3,
          },
          {
            key: 'url',
            label: this.$t('backoffice.email.url') || 'Main URL',
            type: 'url',
            placeholder: this.$t('backoffice.email.placeholder.url') || 'Enter main URL',
            required: false,
            fullWidth: true,
          },
          {
            key: 'code',
            label: this.$t('backoffice.email.code') || 'Verification Code',
            type: 'text',
            placeholder: this.$t('backoffice.email.placeholder.code') || 'Enter verification code',
            required: false,
          }
        );
      }

      return fields;
    },
    showElementType() {
      return ['moderation', 'ModerationPending'].includes(this.form.emailType);
    },
    canSubmit() {
      const hasRequiredFields = this.form.emailType && 
        this.form.communitySelected && 
        this.form.language &&
        (!this.showElementType || this.form.elementType);
      
      // Validate member selection based on mode
      let hasValidMemberSelection = false;
      if (this.supportsMultipleRecipients) {
        hasValidMemberSelection = Array.isArray(this.form.memberSelected) && this.form.memberSelected.length > 0;
      } else {
        hasValidMemberSelection = this.form.memberSelected;
      }
      
      // Additional validation for custom member (only for single recipient mode)
      const hasValidMember = this.supportsMultipleRecipients || !this.isCustomMember 
        ? true 
        : (this.form.customMember.name && this.form.customMember.email);
      
      const hasValidTemplateData = this.conditionalFields.every(field => {
        if (field.required) {
          return this.form.templateData[field.key];
        }
        return true;
      });

      return hasRequiredFields && hasValidMemberSelection && hasValidMember && hasValidTemplateData;
    },
    communitySlug() {
      return this.selectedCommunity?.slug || this.form.communitySelected || '';
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    // Check if custom member is selected
    isCustomMember() {
      return this.form.memberSelected === 'custom';
    },
    // Combine regular members with custom member option
    allMemberOptions() {
      const customOption = {
        key: 'custom',
        name: this.$t('backoffice.email.custom-member') || 'Custom Member',
        email: null,
        avatarURL: null,
        originalData: null
      };
      
      return [customOption, ...this.memberOptions];
    },
    // Organized fields for custom-message email type
    basicStructureFields() {
      return this.conditionalFields.filter(field => 
        ['subject', 'preheader', 'title', 'banner', 'salutation', 'sectionTitle'].includes(field.key)
      );
    },
    section1Fields() {
      return this.conditionalFields.filter(field => 
        field.key.startsWith('section1')
      );
    },
    section2Fields() {
      return this.conditionalFields.filter(field => 
        field.key.startsWith('section2')
      );
    },
    otherFields() {
      return this.conditionalFields.filter(field => 
        ['terms', 'url', 'code'].includes(field.key)
      );
    },
    supportsMultipleRecipients() {
      // Email types that don't require user-specific information can support multiple recipients
      const multiRecipientTypes = [
        'invite-sub', 'welcome-sub', 'missing-member', 'inactive-member', 
        'verify-email', 'security-alert', 'password-reset', 'spaceCreated', 
        'moderation', 'ModerationPending', 'export-completion', 'test-email', 
        'custom-message'
      ];
      return multiRecipientTypes.includes(this.form.emailType);
    },
    selectedMembers() {
      if (this.supportsMultipleRecipients && Array.isArray(this.form.memberSelected)) {
        return this.form.memberSelected.map(key => {
          if (key === 'custom') {
            return {
              key: 'custom',
              name: this.form.customMember.name,
              email: this.form.customMember.email,
              username: this.form.customMember.username,
              avatarURL: null
            };
          }
          return this.memberOptions.find(m => m.key === key);
        }).filter(Boolean);
      }
      return [];
    },
  },
  watch: {
    'form.emailType'(newType, oldType) {
      if (newType !== oldType) {
        this.form.templateData = {};
        this.form.elementType = '';
        
        // Reset member selection when switching email types
        if (this.supportsMultipleRecipients) {
          this.form.memberSelected = [];
        } else {
          this.form.memberSelected = '';
        }
        this.selectedMember = null;
        this.form.customMember = {
          name: '',
          email: '',
          username: '',
        };
        
        // Initialize sessions array for security-alert
        if (newType === 'security-alert') {
          this.$set(this.form.templateData, 'differentDeviceSessions', []);
        }
      }
    },

  },
  beforeDestroy() {
    // Clean up search timeout to prevent memory leaks
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    window.removeEventListener('resize', this.handleResize);
  },
  async created() {
    if (this.isNexos) {
      this.form.language = 'es';
    }
    window.addEventListener('resize', this.handleResize);
    this.handleResize();

    try {
      // 1) Get the slug from the URL path
      const currentSpaceSlug = this.getCurrentSpaceSlug();
      console.log('current space slug', currentSpaceSlug);
      
      if (!currentSpaceSlug) {
        console.warn('No space slug found in URL');
        return;
      }

      // 2) Get the current space by slug
      const currentSpace = await this.fetchSpaceBySlug(currentSpaceSlug);
      console.log('current space', currentSpace);
      
      if (!currentSpace) {
        console.warn('Current space not found');
        return;
      }

      this.currentSpace = currentSpace;
      
      // 3) Get children spaces
      const childrenSpaces = await this.fetchChildrenSpaces(currentSpace.slug);
      console.log('children spaces', childrenSpaces);
      
      // 4) Create community options (current space first, then children)
      this.communityOptions = [
        {
          key: currentSpace.key,
          name: translateTranslationTable(this.currentLocale, currentSpace.name),
          slug: currentSpace.slug,
          isCurrent: true
        }
      ];

      // Add children spaces if any exist
      if (Array.isArray(childrenSpaces) && childrenSpaces.length > 0) {
        const childrenOptions = childrenSpaces.map(space => ({
          key: space.key,
          name: translateTranslationTable(this.currentLocale, space.name),
          slug: space.slug,
          isCurrent: false
        }));
        this.communityOptions.push(...childrenOptions);
      }
      
      // 5) Pre-select the current space
      this.form.communitySelected = currentSpace.slug;
      this.selectedCommunity = currentSpace;
      
      // 6) Load members for the current space
      await this.loadMembersForCommunity(currentSpace.slug);
      
    } catch (error) {
      console.error('Error initializing send email component:', error);
    }
  },
  destroyed() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleResize() {
      this.isSmall = window.innerWidth < 500;
    },
    getCurrentSpaceSlug() {
      // Extract slug from URL path like /nexos/backoffice/send-email
      const path = this.$route.path;
      const pathSegments = path.split('/').filter(segment => segment);
      
      // The first segment should be the space slug
      return pathSegments[0] || null;
    },

    // Fetch space by slug using store dispatch (consistent with other components)
    async fetchSpaceBySlug(slug) {
      try {
        const response = await this.$store.dispatch('getItems', {
          itemType: "spaces",
          customName: "spaces",
          requestConfig: {
            slug: slug,
          }
        });
        
        console.log('fetchSpaceBySlug response:', response);
        
        // Handle different response structures
        let spaces = [];
        if (response && response.data) {
          spaces = response.data;
        } else if (Array.isArray(response)) {
          spaces = response;
        } else if (response && Array.isArray(response.items)) {
          spaces = response.items;
        } else {
          console.warn('Unexpected response structure for space by slug:', response);
          return null;
        }
        
        return spaces && spaces.length > 0 ? spaces[0] : null;
      } catch (error) {
        console.error('Error fetching space by slug:', error);
        return null;
      }
    },
    // Fetch children spaces using store dispatch (consistent with other components)
    async fetchChildrenSpaces(parentSpaceSlug) {
      try {
        const response = await this.$store.dispatch('getItems', {
          itemType: "spaces",
          customName: "spaces",
          requestConfig: {
            parentSlug: parentSpaceSlug,
          }
        });
        
        console.log('fetchChildrenSpaces response:', response);
        
        // Handle different response structures
        if (response && response.data) {
          return response.data;
        } else if (Array.isArray(response)) {
          return response;
        } else if (response && Array.isArray(response.items)) {
          return response.items;
        } else {
          console.warn('Unexpected response structure for children spaces:', response);
          return [];
        }
      } catch (error) {
        console.error('Error fetching children spaces:', error);
        return [];
      }
    },

    // Load members for a specific community
    async loadMembersForCommunity(communitySlug) {
      if (!communitySlug) {
        this.memberOptions = [];
        return;
      }

      try {
        const response = await this.$store.dispatch('getItems', {
          itemType: 'communityMembers',
          forceAPICall: true,
          page: 1,
          perPage: 100,
          requestConfig: {
            communitySlug: communitySlug,
            isForBackoffice: true,
          },
        });

        this.memberOptions = (response.data || []).map(member => ({
          key: member.key,
          name: member.name && member.surname 
            ? `${member.name} ${member.surname}`
            : member.username,
          email: member.email,
          username: member.username,
          avatarURL: member.avatarURL,
          originalData: member
        }));
      } catch (error) {
        console.error('Error loading members:', error);
        this.memberOptions = [];
      }
    },
    // Search members (for v-select search functionality)
    async searchMembers(search) {
      if (!this.form.communitySelected || !search) {
        return;
      }

      // Clear previous timeout to prevent race conditions
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Debounce the search to prevent excessive API calls
      this.searchTimeout = setTimeout(async () => {
        // Prevent duplicate searches for the same term
        if (this.lastSearchTerm === search) {
          return;
        }

        this.lastSearchTerm = search;
        this.isSearching = true;

        try {
          const response = await this.$store.dispatch('getItems', {
            itemType: 'communityMembers',
            forceAPICall: true,
            page: 1,
            perPage: 50,
            requestConfig: {
              searchString: search,
              searchEmail: search,
              communitySlug: this.form.communitySelected,
              isForBackoffice: true,
            },
          });

          // Only update if this is still the current search term
          if (this.lastSearchTerm === search) {
            const members = response.data || [];
            
            console.log('Search results for:', search, 'Found members:', members);
            
            // Safely map member data with error handling
            this.memberOptions = members.map(member => {
              try {
                let displayName = member.username || 'Unknown User';
                
                // NEVER translate person names - they are proper nouns
                if (member.name && member.surname) {
                  displayName = `${member.name} ${member.surname}`;
                } else if (member.name) {
                  displayName = member.name;
                }

                const mappedMember = {
                  key: member.key,
                  name: displayName,
                  email: member.email,
                  username: member.username,
                  avatarURL: member.avatarURL,
                  originalData: member
                };
                
                console.log('Mapped member:', mappedMember);
                return mappedMember;
              } catch (error) {
                console.warn('Error processing member data:', error, member);
                return {
                  key: member.key,
                  name: member.username || member.email || 'Unknown User',
                  email: member.email,
                  username: member.username,
                  avatarURL: member.avatarURL,
                  originalData: member
                };
              }
            });
            
            console.log('Final memberOptions:', this.memberOptions);
            
            // If no results found, show a message
            if (this.memberOptions.length === 0) {
              console.log('No members found for search:', search);
            }
          }
        } catch (error) {
          console.error('Error searching members:', error);
          // Don't clear memberOptions on error to preserve existing results
        } finally {
          this.isSearching = false;
        }
      }, 300); // 300ms debounce delay
    },

    addSession() {
      if (!this.form.templateData.differentDeviceSessions) {
        this.$set(this.form.templateData, 'differentDeviceSessions', []);
      }
      this.form.templateData.differentDeviceSessions.push({
        browser: '',
        os: '',
        lastActivity: '',
      });
    },
    removeSession(index) {
      this.form.templateData.differentDeviceSessions.splice(index, 1);
    },
    toggleSection(sectionKey) {
      this.collapsedSections[sectionKey] = !this.collapsedSections[sectionKey];
    },
    handleCommunitySelect(selectedSlug) {
      this.selectedCommunity = this.communityOptions.find(c => c.slug === selectedSlug);
      // Reset member selection when community changes
      this.form.memberSelected = this.supportsMultipleRecipients ? [] : '';
      this.selectedMember = null;
      this.memberOptions = [];
      
      // Clear search state
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.lastSearchTerm = '';
      this.isSearching = false;
      
      // Load members for the new community
      if (selectedSlug) {
        this.loadMembersForCommunity(selectedSlug);
      }
    },
    handleMemberSelect(selectedKey) {
      if (this.supportsMultipleRecipients) {
        // For multiple recipients, selectedKey will be an array
        if (Array.isArray(selectedKey)) {
          // Handle multiple selection
          this.form.memberSelected = selectedKey;
        } else {
          // Handle single selection in multiple mode
          this.form.memberSelected = selectedKey ? [selectedKey] : [];
        }
      } else {
        // Single recipient mode
        if (selectedKey === 'custom') {
          // Custom member selected - clear the fields for manual entry
          this.selectedMember = null;
          this.form.customMember = {
            name: '',
            email: '',
            username: '',
          };
        } else {
          // Regular member selected - populate custom fields with member data
          this.selectedMember = this.memberOptions.find(m => m.key === selectedKey);
          if (this.selectedMember) {
            this.form.customMember = {
              name: this.selectedMember.name,
              email: this.selectedMember.email,
              username: this.selectedMember.username || this.selectedMember.name,
            };
          }
        }
      }
    },
    handleSearchBlur() {
      // Clear search state when user stops typing
      this.isSearching = false;
    },
    customFilter(option, label, search) {
      // Search by name, email, and username
      const searchLower = search.toLowerCase();
      const nameMatch = option.name && option.name.toLowerCase().includes(searchLower);
      const emailMatch = option.email && option.email.toLowerCase().includes(searchLower);
      const usernameMatch = option.username && option.username.toLowerCase().includes(searchLower);
      
      return nameMatch || emailMatch || usernameMatch;
    },
    removeSelectedMember(key) {
      if (this.supportsMultipleRecipients && Array.isArray(this.form.memberSelected)) {
        this.form.memberSelected = this.form.memberSelected.filter(k => k !== key);
      }
    },
    validateForm() {
      // Reset validation
      Object.keys(this.validation).forEach(key => {
        this.validation[key] = null;
      });

      // Validate required fields
      this.validation.emailType = this.form.emailType ? null : false;
      this.validation.communitySelected = this.form.communitySelected ? null : false;
      
      // Validate member selection based on mode
      if (this.supportsMultipleRecipients) {
        this.validation.memberSelected = Array.isArray(this.form.memberSelected) && this.form.memberSelected.length > 0 ? null : false;
      } else {
        this.validation.memberSelected = this.form.memberSelected ? null : false;
      }
      
      this.validation.language = this.form.language ? null : false;

      if (this.showElementType) {
        this.validation.elementType = this.form.elementType ? null : false;
      }

      // Validate custom member fields if custom member is selected
      if (this.isCustomMember) {
        this.validation.customMemberName = this.form.customMember.name ? null : false;
        this.validation.customMemberEmail = this.form.customMember.email ? null : false;
        // Validate email format
        if (this.form.customMember.email) {
          const emailValid = validatorEmailValidator(this.form.customMember.email);
          if (!emailValid) {
            this.validation.customMemberEmail = false;
          }
        }
      } else {
        this.validation.customMemberName = null;
        this.validation.customMemberEmail = null;
      }

      // Validate conditional fields
      this.conditionalFields.forEach(field => {
        if (field.required) {
          const value = this.form.templateData[field.key];
          this.$set(this.validation, field.key, value ? null : false);
        }
      });

      return Object.values(this.validation).every(v => v !== false);
    },
    async handleSubmit() {
      console.log('handleSubmit');
      
      if (!this.validateForm()) {
        this.notifyError(this.$t('form.validation.please-fill-required-fields') || 'Please fill in all required fields');
        return;
      }

      this.isLoading = true;

      try {
        if (!this.selectedCommunity) {
          throw new Error('Community must be selected');
        }

        if (this.supportsMultipleRecipients) {
          if (!Array.isArray(this.form.memberSelected) || this.form.memberSelected.length === 0) {
            throw new Error('At least one member must be selected');
          }
        } else {
          if (!this.selectedMember) {
            throw new Error('Member must be selected');
          }
        }

        // Prepare recipients data - always use array format for consistency
        let recipientsData;
        if (this.supportsMultipleRecipients) {
          // Multiple recipients
          recipientsData = this.form.memberSelected.map(key => {
            if (key === 'custom') {
              return {
                name: this.form.customMember.name,
                email: this.form.customMember.email,
                username: this.form.customMember.username || this.form.customMember.name,
              };
            } else {
              const member = this.memberOptions.find(m => m.key === key);
              return {
                name: member.name,
                email: member.email,
                username: member.username || member.name,
              };
            }
          });
        } else {
          // Single recipient - still use array format
          const recipientData = this.isCustomMember ? {
            name: this.form.customMember.name,
            email: this.form.customMember.email,
            username: this.form.customMember.username || this.form.customMember.name,
          } : {
            name: this.selectedMember.name,
            email: this.selectedMember.email,
            username: this.selectedMember.username || this.selectedMember.name,
          };
          recipientsData = [recipientData];
        }

        const emailData = {
          recipients: recipientsData,
          communitySlug: this.selectedCommunity.slug,
          emailType: this.form.emailType,
          language: this.form.language,
          templateData: { ...this.form.templateData },
          testMode: this.form.testMode,
          ...(this.showElementType && { elementType: this.form.elementType }),
        };

        console.log('Sending email with data:', emailData);

        const isTesting = false;
        let response;

        if(isTesting){
          console.log('TEST MODE: Sending test email');

          // fake response
          response = {
            success: true,
            message: 'Test email sent successfully',
            emailData: {
              subject: 'Test Subject',
              destination: 'test@test.com',
              origin: 'test@test.com',
              html: 'Test HTML',
              templateData: {},
            },
          };
        } else {
          console.log('PRODUCTION MODE: Sending real email');

          response = await sendEmail(emailData);
        }

        console.log('Response:', response);
        
        if (response.success) {
          const message = this.form.testMode 
            ? (this.$t('backoffice.email.email-tested-successfully') || 'Email tested successfully - no email was sent')
            : (this.$t('backoffice.email.email-sent-successfully') || 'Email sent successfully');
          this.notifySuccess(message);

          // Reset form
          this.resetForm();
        } else {
          throw new Error(response.message || 'Failed to send email');
        }
      } catch (error) {
        console.error('Error sending email:', error);
        this.notifyError(error.message || this.$t('error-message.general-error') || 'An error occurred while sending the email');
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.form = {
        emailType: '',
        communitySelected: this.currentSpace?.slug || '',
        memberSelected: this.supportsMultipleRecipients ? [] : '',
        language: this.isNexos ? 'es' : 'en',
        templateData: {},
        elementType: '',
        testMode: false,
        customMember: {
          name: '',
          email: '',
          username: '',
        },
      };
      this.selectedCommunity = this.currentSpace;
      this.selectedMember = null;
      this.memberOptions = [];
      Object.keys(this.validation).forEach(key => {
        this.validation[key] = null;
      });
      
      // Reload members for the current space
      if (this.currentSpace) {
        this.loadMembersForCommunity(this.currentSpace.slug);
      }
    },
  },
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.border {
  border: 1px solid #dee2e6;
}

.rounded {
  border-radius: 0.375rem;
}

section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 1rem;
}

section h5 {
  color: #495057;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.pointer {
  cursor: pointer;
}

.custom-switch-lg .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #ffc107;
  border-color: #ffc107;
}

.custom-switch-lg .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 0.2rem rgba(255, 193, 7, 0.25);
}

.custom-switch-lg .custom-control-label {
  font-size: 1.1rem;
}

/* Custom Message Form Styling */
.custom-message-form {
  max-width: 100%;
}

.field-section {
  background-color: #ffffff;
  border: 1px solid #e3e6f0;
  border-radius: 0.35rem;
  padding: 1.5rem;
  margin-bottom: 1rem;
}

.section-header-row {
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e3e6f0;
}

.section-header {
  color: #5a5c69;
  font-weight: 600;
  font-size: 1.1rem;
}

.toggle-btn {
  border: 1px solid #d1d3e2;
  transition: all 0.15s ease-in-out;
}

.toggle-btn:hover {
  background-color: #f8f9fc;
  border-color: #bac8f3;
}

.fields-container {
  background-color: #f8f9fc;
  border-radius: 0.25rem;
  padding: 1rem;
}

.field-item {
  width: 100%;
}

.field-item .form-group {
  margin-bottom: 0;
}

/* Smooth collapse animation */
.collapse-enter-active, .collapse-leave-active {
  transition: all 0.3s ease;
}

.collapse-enter, .collapse-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

@media (max-width: 768px) {
  .field-section {
    padding: 1rem;
  }
  
  .fields-container {
    padding: 0.75rem;
  }
}

/* Selected members styling */
.selected-members-list {
  max-height: 300px;
  overflow-y: auto;
}

.selected-member-item {
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.selected-member-item:hover {
  background-color: #e9ecef;
}

.selected-member-item .btn {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.selected-member-item:hover .btn {
  opacity: 1;
}
</style> 